import { Badge } from "@/components/ui/badge";
import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Building2, Zap, Wind, Target, Snowflake, Calendar, Wrench, Shield, ThermometerSun, Fan, Cog, Thermometer, ShoppingCart, Search, X } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";
import ServiceBookingDialog from "./ServiceBookingDialog";

const CATEGORIES = ["All", "AC Systems", "Service & AMC", "Repair", "Installation & Projects"];

// Standard warranty for U-Bend / Gas-Leak fabrication (on proper service maintenance)
const UBEND_WARRANTY = "Extended warranty valid only on booking an AMC with us - Residential: 5 years; Commercial: 3 years; Industrial: 3 years. Sea/Khadi/Naal/Industrial damage zones (Sachin, Pandesera, Bhestan, Vesu, Khatodara, Hazira, Althan, Nahar Road, near Tapi River / sea side, etc.): 2 years. Without an AMC: standard 1 year warranty. All warranties apply on proper service maintenance.";

const SERVICE_CATEGORY = {
  "Air Chiller Systems": "AC Systems",
  "VRF Systems": "AC Systems",
  "Ductable AC": "AC Systems",
  "Cassette AC": "AC Systems",
  "Split AC": "AC Systems",
  "Annual Maintenance": "Service & AMC",
  "AC Leakage & Breakdown Repair": "Repair",
  "Copper Piping Projects": "Installation & Projects",
  "Inverter to Fixed Speed Conversion": "Repair",
  "AC PCB Circuit Repair & Sale": "Repair",
  "Ducting Work": "Installation & Projects",
  "Interior Design & HVAC Layout": "Installation & Projects",
  "AHU (Air Handling Unit) Sale & Service": "AC Systems",
  "Compressor (New & Refurbished)": "Repair",
  "Industrial Cooler": "AC Systems",
};

const ServicesSection = () => {
  const [bookingService, setBookingService] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const services = [
    {
      icon: <Building2 className="h-10 w-10" />,
      title: "Air Chiller Systems",
      description: "Industrial and commercial chiller installation, repair and maintenance services for large-scale cooling requirements.",
      image: "/images/services/air-chiller.jpeg",
      features: ["Installation", "Repair", "AMC"],
      booking: [
        { name: "Problem Inspection - Chiller", rate: 3000, unit: "visit", details: "On-site fault diagnosis for chiller systems. Amount deducted from final bill when you book the repair." },
        { name: "Normal Service - Chiller", rate: 0, quote: true, unit: "unit", details: "Complete chiller servicing. Price depends on capacity & site conditions." },
        { name: "Deep Clean / Chemical Wash - Chiller", rate: 0, quote: true, unit: "unit", details: "Coil & condenser deep cleaning for large chiller units." },
        { name: "AMC Contract - Chiller", rate: 0, quote: true, unit: "year", details: "Annual maintenance contract with periodic service & priority support." },
        { name: "Chiller Spare Parts", rate: 0, quote: true, unit: "item", details: "Genuine / compatible spare parts as per requirement." },
      ]
    },
    {
      icon: <Zap className="h-10 w-10" />,
      title: "VRF Systems",
      description: "Variable Refrigerant Flow systems for efficient climate control in commercial buildings and large spaces.",
      image: "/images/services/vrf-system.jpg",
      features: ["Installation", "Service", "Pan India"],
      booking: [
        { name: "Problem Inspection - VRF/VRV", rate: 2000, unit: "visit", details: "Fault diagnosis for VRF/VRV systems (8 Ton & above, Inverter). Deducted from final repair bill." },
        { name: "Normal Service (8.5 Ton)", rate: 1500, unit: "unit", details: "Filter/panel cleaning, coil & blower dusting, electrical check & testing." },
        { name: "Normal Service (11 Ton)", rate: 2000, unit: "unit" },
        { name: "Normal Service (16.5 Ton)", rate: 2600, unit: "unit" },
        { name: "Normal Service (17-22 Ton)", rate: 3000, unit: "unit" },
        { name: "Normal Service (22 Ton & above)", rate: 0, quote: true, unit: "unit" },
        { name: "Water Jet Deep Clean (11 Ton)", rate: 2500, unit: "unit", details: "High-pressure water-jet coil & blower cleaning." },
        { name: "Water Jet Deep Clean (16.5 Ton)", rate: 3500, unit: "unit" },
        { name: "Water Jet Deep Clean (22 Ton)", rate: 4000, unit: "unit" },
        { name: "Chemical Wash (8 Ton)", rate: 2500, unit: "unit", details: "Deep chemical wash of coil & blower." },
        { name: "Chemical Wash (11 Ton)", rate: 3000, unit: "unit" },
        { name: "Chemical Wash (16.5 Ton)", rate: 4000, unit: "unit" },
        { name: "Chemical Wash (22 Ton)", rate: 4800, unit: "unit" },
        { name: "AMC Contract (8.5 Ton)", rate: 7000, unit: "year", details: "1 Normal Service + 2 Deep Clean + 2 Free Inspection visits per year." },
        { name: "AMC Contract (11 Ton)", rate: 8000, unit: "year" },
        { name: "AMC Contract (16.5 Ton)", rate: 15000, unit: "year" },
        { name: "AMC Contract (22 Ton)", rate: 20000, unit: "year" },
        { name: "AMC Contract (23 Ton & above)", rate: 0, quote: true, unit: "year" },
        { name: "New Installation", rate: 1000, unit: "ton", details: "Installation charge per ton of capacity." },
        { name: "Copper Piping (8.5 Ton)", rate: 1000, unit: "running ft", details: "Copper pipe supply & laying per running foot." },
        { name: "Copper Piping (11 Ton & above)", rate: 0, quote: true, unit: "running ft" },
        { name: "PCB Circuit Repair", rate: 0, quote: true, unit: "board", details: "IDU/ODU PCB repair for VRF/VRV systems." },
        { name: "Gas Leak / U-Bend Repair", rate: 0, quote: true, unit: "unit", details: "Leak detection & U-bend fabrication for VRF/VRV." },
        { name: "Motor Repair (DC Motor)", rate: 0, quote: true, unit: "unit" },
      ]
    },
    {
      icon: <Wind className="h-10 w-10" />,
      title: "Ductable AC",
      description: "Centralized ductable air conditioning solutions for offices, showrooms and commercial establishments.",
      image: "/images/services/ductable-ac.jpg",
      features: ["Installation", "Repair", "Pan India"],
      booking: [
        { name: "Problem Inspection (3-5.5 Ton)", rate: 1000, unit: "visit", details: "Fault diagnosis for Ductable AC. Deducted from final repair bill." },
        { name: "Problem Inspection (8 Ton & above)", rate: 1500, unit: "visit" },
        { name: "Normal Service (3-4 Ton)", rate: 1000, unit: "unit", details: "Filter/panel cleaning, coil & blower dusting, electrical check & testing." },
        { name: "Normal Service (5-6 Ton)", rate: 1200, unit: "unit" },
        { name: "Normal Service (8.5 Ton)", rate: 1500, unit: "unit" },
        { name: "Normal Service (11 Ton)", rate: 2000, unit: "unit" },
        { name: "Normal Service (16.5 Ton)", rate: 2600, unit: "unit" },
        { name: "Normal Service (17-22 Ton)", rate: 3000, unit: "unit" },
        { name: "Water Jet Deep Clean (3 Ton)", rate: 1200, unit: "unit", details: "High-pressure water-jet coil & blower cleaning." },
        { name: "Water Jet Deep Clean (4 Ton)", rate: 1600, unit: "unit" },
        { name: "Water Jet Deep Clean (5-6 Ton)", rate: 1500, unit: "unit" },
        { name: "Water Jet Deep Clean (8.5 Ton)", rate: 2000, unit: "unit" },
        { name: "Water Jet Deep Clean (11 Ton)", rate: 2500, unit: "unit" },
        { name: "Chemical Wash (3 Ton)", rate: 1800, unit: "unit", details: "Deep chemical wash of coil & blower." },
        { name: "Chemical Wash (up to 4 Ton)", rate: 2000, unit: "unit" },
        { name: "Chemical Wash (5-6 Ton)", rate: 2300, unit: "unit" },
        { name: "Chemical Wash (8 Ton)", rate: 2500, unit: "unit" },
        { name: "AMC Contract (5-6 Ton)", rate: 5000, unit: "year", details: "1 Normal Service + 2 Deep Clean + 2 Free Inspection visits per year." },
        { name: "AMC Contract (8.5 Ton)", rate: 7000, unit: "year" },
        { name: "New Installation (above 3 Ton)", rate: 1000, unit: "ton", details: "Installation charge per ton of capacity." },
        { name: "Copper Piping (3-4 Ton)", rate: 580, unit: "running ft", details: "Copper pipe supply & laying per running foot." },
        { name: "Copper Piping (5.5-6 Ton)", rate: 700, unit: "running ft" },
        { name: "Copper Piping (8.5 Ton)", rate: 1000, unit: "running ft" },
        { name: "PCB Circuit Repair", rate: 0, quote: true, unit: "board", details: "IDU/ODU PCB repair for Ductable systems." },
        { name: "Gas Leak / U-Bend Repair", rate: 0, quote: true, unit: "unit", details: "Leak detection & U-bend fabrication." },
      ]
    },
    {
      icon: <Target className="h-10 w-10" />,
      title: "Cassette AC",
      description: "Ceiling-mounted cassette AC units perfect for offices, restaurants and commercial spaces with false ceilings.",
      image: "/images/services/cassette-ac.jpg",
      features: ["Installation", "Service", "Repair"],
      booking: [
        { name: "Inspection Charge - 1 Way / 4 Way (1.5-2 Ton)", rate: 700, unit: "visit", details: "Problem inspection & detection, then a clear solution pathway.", warranty: "This amount is deducted from your final bill when you book the repair." },
        { name: "Inspection Charge - 1 Way / 4 Way (2.1-4 Ton)", rate: 850, unit: "visit", details: "Problem inspection & detection, then a clear solution pathway.", warranty: "This amount is deducted from your final bill when you book the repair." },
        { name: "1 Way - Water Leakage Solution (1.5/2 Ton)", rate: 800, unit: "unit", details: "Problem inspection & detection, then a solution pathway.", warranty: "15 days service warranty." },
        { name: "4 Way - Water Leakage Solution", rate: 1000, unit: "unit", details: "Problem inspection & detection, then a solution pathway.", warranty: "15 days service warranty." },
        { name: "Normal Dry Service - 1 Way (1.5/2 Ton)", rate: 750, unit: "unit", details: "Power isolation & inspection, filter & panel cleaning, coil & blower dusting (air blower), electrical check & testing." },
        { name: "Normal Dry Service - 4 Way (1.5/2 Ton)", rate: 850, unit: "unit", details: "Power isolation & inspection, filter & panel cleaning, coil & blower dusting (air blower), electrical check & testing." },
        { name: "Normal Dry Service - 4 Way (2.1-4 Ton)", rate: 1000, unit: "unit", details: "Power isolation & inspection, filter & panel cleaning, coil & blower dusting (air blower), electrical check & testing." },
        { name: "Water Jet Deep Clean Service (1.5/2 Ton)", rate: 900, unit: "unit", details: "Power isolation & inspection, filter & panel cleaning, water-jet coil & blower cleaning, electrical check & testing." },
        { name: "4 Way - Water Jet Deep Clean (2.1-4 Ton)", rate: 1200, unit: "unit", details: "Power isolation & inspection, filter & panel cleaning, water-jet + specialised chemical coil/blower cleaning, electrical check & testing." },
        { name: "Gas Leakage Repair - 1 Way / 4 Way (1.5/2 Ton)", rate: 7500, unit: "unit", details: "IDU check; if leak found, U-bend fabrication. Incl. N2/gas pressure testing, U-bend fabrication + copper sealing, IDU uninstall/reinstall, gas recharging. Freight as per area.", warranty: "U-bend gas warranty (on proper maintenance): Residential 1 year, Commercial 8 months, Khadi/Naal/Sea/Industrial 4 months." },
        { name: "4 Way - Gas Leakage Repair (2.1-4 Ton)", rate: 8000, unit: "unit", details: "IDU check; if leak found, U-bend fabrication. Incl. N2/gas pressure testing, U-bend fabrication + copper sealing, IDU uninstall/reinstall, gas recharging. Freight as per area.", warranty: "U-bend gas warranty (on proper maintenance): Residential 1 year, Commercial 8 months, Khadi/Naal/Sea/Industrial 4 months." },
        { name: "Inverter PCB Circuit Repair (1.5/2 Ton)", rate: 4500, unit: "board", details: "IDU PCB or ODU PCB or both.", warranty: "1 month PCB warranty; void on voltage/current fluctuation or user-induced faults." },
        { name: "Fix Speed PCB Circuit Repair", rate: 3500, unit: "board", details: "IDU PCB or ODU PCB or both.", warranty: "1 month PCB warranty; void on voltage/current fluctuation or user-induced faults." },
      ]
    },
    {
      icon: <Snowflake className="h-10 w-10" />,
      title: "Split AC",
      description: "Residential and commercial split AC installation, service and repair for all major brands.",
      image: "/images/services/split-ac.jpg",
      features: ["All Brands", "Gas Filling", "Repair"],
      booking: [
        { name: "Problem Inspection (1-2.2 Ton)", rate: 500, unit: "visit", details: "Fault diagnosis for Split/Window AC. Deducted from final repair bill." },
        { name: "Normal Service (1-2.2 Ton)", rate: 500, unit: "unit", details: "Filter/panel cleaning, coil & blower dusting, electrical check & testing." },
        { name: "Water Jet Deep Clean (1-2.2 Ton)", rate: 750, unit: "unit", details: "High-pressure water-jet coil & blower cleaning." },
        { name: "Chemical Wash (1-2.2 Ton)", rate: 1800, unit: "unit", details: "Deep chemical wash of coil & blower." },
        { name: "AMC Contract (1-2.2 Ton)", rate: 2000, unit: "year", details: "1 Normal Service + 2 Deep Clean + 2 Free Inspection visits per year." },
        { name: "Water Problem Repair (1-2.2 Ton)", rate: 500, unit: "unit", details: "Water leakage / drainage fault repair." },
        { name: "Fix-Speed PCB Repair (1-2.2 Ton)", rate: 2000, unit: "board", details: "IDU/ODU PCB circuit repair for Fix Speed AC.", warranty: "1 month PCB warranty; void on voltage/current fluctuation." },
        { name: "Inverter PCB Repair (1-2.2 Ton)", rate: 0, quote: true, unit: "board", details: "Approx Rs.3,500 - Rs.4,500 depending on model.", warranty: "1 month PCB warranty; void on voltage/current fluctuation." },
        { name: "Fix-Speed Motor Repair (1-2.2 Ton)", rate: 2000, unit: "unit", details: "Indoor/outdoor fan motor repair for Fix Speed AC." },
        { name: "Inverter Motor Repair (1-2.2 Ton)", rate: 0, quote: true, unit: "unit" },
        { name: "Gas Leak U-Bend - 1 Ton (IDU+ODU)", rate: 5000, unit: "unit", details: "Leak detection, U-bend fabrication, N2/gas pressure test & recharge.", warranty: UBEND_WARRANTY },
        { name: "Gas Leak U-Bend - 1.5/2 Ton (IDU)", rate: 5500, unit: "unit", warranty: UBEND_WARRANTY },
        { name: "Copper Piping (1-2.2 Ton)", rate: 380, unit: "running ft", details: "Copper pipe supply & laying per running foot." },
        { name: "New Installation (1-2.2 Ton)", rate: 2000, unit: "unit", details: "Standard IDU + ODU installation." },
        { name: "AC Shifting (Open + Fitting)", rate: 2500, unit: "unit", details: "Uninstall + reinstall (IDU+ODU) at new location." },
      ]
    },
    {
      icon: <Calendar className="h-10 w-10" />,
      title: "Annual Maintenance",
      description: "Comprehensive Annual Maintenance Contracts (AMC) to keep your AC systems running efficiently year-round.",
      image: "/images/services/annual-maintenance.jpg",
      features: ["Regular Checkups", "Priority Service", "Cost Savings"],
      booking: [
        { name: "AMC - Split/Window AC (1-2.2 Ton)", rate: 2000, unit: "year", details: "1 Normal Service + 2 Deep Clean Service + 2 Free Inspection Visits per year." },
        { name: "AMC - Split/Tower/Ductable (3 Ton)", rate: 2700, unit: "year" },
        { name: "AMC - Cassette AC (1-2 Ton)", rate: 3000, unit: "year" },
        { name: "AMC - Cassette AC (3-4 Ton)", rate: 4500, unit: "year" },
        { name: "AMC - Cassette/Tower/Ductable (5-6 Ton)", rate: 5000, unit: "year" },
        { name: "AMC - Ductable/VRF (8.5 Ton)", rate: 7000, unit: "year" },
        { name: "AMC - Ductable/VRF (11 Ton)", rate: 8000, unit: "year" },
        { name: "AMC - Ductable/VRF (16.5 Ton)", rate: 15000, unit: "year" },
        { name: "AMC - Ductable/VRF (22 Ton)", rate: 20000, unit: "year" },
        { name: "AMC - Ductable/VRF (23 Ton & above)", rate: 0, quote: true, unit: "year" },
      ]
    },
    {
      icon: <Wrench className="h-10 w-10" />,
      title: "AC Leakage & Breakdown Repair",
      description: "Expert repair services for VRF, Ductable and Split AC leakage issues and emergency breakdown repairs.",
      image: "/images/services/ac-repair.png",
      features: ["VRF Repair", "Ductable", "Split AC", "Emergency"],
      booking: [
        { name: "U-Bend Fabrication - Split 1 Ton (IDU+ODU)", rate: 5000, unit: "unit", details: "Leak detection, U-bend fabrication, N2/gas pressure test & recharge.", warranty: UBEND_WARRANTY },
        { name: "U-Bend Fabrication - Split 1.5/2 Ton (IDU)", rate: 5500, unit: "unit", warranty: UBEND_WARRANTY },
        { name: "U-Bend Fabrication - Window AC (1-2 Ton)", rate: 5000, unit: "unit", warranty: UBEND_WARRANTY },
        { name: "U-Bend Fabrication - Cassette/Tower/Ductable (up to 3 Ton)", rate: 7500, unit: "unit", warranty: UBEND_WARRANTY },
        { name: "U-Bend Fabrication - Larger / VRF Systems", rate: 0, quote: true, unit: "unit" },
        { name: "Water Problem Repair - Split/Window", rate: 500, unit: "unit", details: "Water leakage / drainage fault repair." },
        { name: "Water Problem Repair - Cassette/Tower/Ductable (3-5.5 Ton)", rate: 1000, unit: "unit" },
        { name: "Water Problem Repair - Larger / VRF", rate: 0, quote: true, unit: "unit" },
      ]
    },
    {
      icon: <Shield className="h-10 w-10" />,
      title: "Copper Piping Projects",
      description: "Professional copper piping installation and replacement for Air Chillers, VRF, Ductable and Split AC systems.",
      image: "/images/services/copper-piping.jpg",
      features: ["Air Chiller", "VRF", "Ductable", "Split AC"],
      booking: [
        { name: "Copper Piping - Split AC (1-2.2 Ton)", rate: 380, unit: "running ft", details: "Copper pipe supply & laying per running foot." },
        { name: "Copper Piping - Split/Cassette/Tower/Ductable (3-4 Ton)", rate: 580, unit: "running ft" },
        { name: "Copper Piping - Split/Cassette/Tower/Ductable (5.5-6 Ton)", rate: 700, unit: "running ft" },
        { name: "Copper Piping - Ductable/VRF (8.5 Ton)", rate: 1000, unit: "running ft" },
        { name: "Copper Piping - Ductable/VRF (11 Ton & above)", rate: 0, quote: true, unit: "running ft" },
        { name: "Copper Piping - Ductable/VRF (16.5 Ton & above)", rate: 0, quote: true, unit: "running ft" },
      ]
    },
    {
      icon: <ThermometerSun className="h-10 w-10" />,
      title: "Inverter to Fixed Speed Conversion",
      description: "Convert VRF, Ductable, Cassette and Split Inverter AC systems to reliable Fixed Speed AC systems.",
      image: "/images/services/inverter-conversion.jpeg",
      features: ["VRF", "Ductable", "Cassette", "Split AC"],
      booking: [
        { name: "1 Ton AC Conversion", rate: 10000, unit: "unit", details: "Inverter to Fixed Speed conversion for 1 Ton AC (PCB/compressor conversion, testing & commissioning)." },
        { name: "1.5 Ton AC Conversion", rate: 12000, unit: "unit", details: "Inverter to Fixed Speed conversion for 1.5 Ton AC." },
        { name: "2 Ton AC Conversion", rate: 13000, unit: "unit", details: "Inverter to Fixed Speed conversion for 2 Ton AC." },
        { name: "More than 2 Ton", rate: 0, quote: true, unit: "unit", details: "Conversion for above 2 Ton (Cassette / Ductable / VRF). Priced after site assessment." },
      ]
    },
    {
      icon: <Zap className="h-10 w-10" />,
      title: "AC PCB Circuit Repair & Sale",
      description: "Expert PCB circuit board repair and new PCB sales for all types of air conditioning systems.",
      image: "/images/services/pcb-repair.jpeg",
      features: ["PCB Repair", "New PCB Sale", "All AC Types"],
      booking: [
        { name: "PCB Repair - Fix Speed Split/Window (1-2.2 Ton)", rate: 2000, unit: "board", details: "IDU/ODU PCB circuit repair for Fix Speed AC.", warranty: "1 month PCB warranty; void on voltage/current fluctuation." },
        { name: "PCB Repair - Inverter Split/Window (1-2.2 Ton)", rate: 0, quote: true, unit: "board", details: "Approx Rs.3,500 - Rs.4,500 depending on model.", warranty: "1 month PCB warranty; void on voltage/current fluctuation." },
        { name: "PCB Repair - Cassette/Tower/Ductable/VRF", rate: 0, quote: true, unit: "board", details: "IDU/ODU PCB repair for larger systems." },
      ]
    },
    {
      icon: <Wind className="h-10 w-10" />,
      title: "Ducting Work",
      description: "Professional ducting services for Ductable AC systems including open ceiling concept and exposed duct designs for modern spaces.",
      image: "/images/services/ducting-work.png",
      features: ["Ductable AC", "Open Ceiling", "Exposed Duct"],
      booking: [
        { name: "Duct Manufacturing & Installation", rate: 0, quote: true, unit: "project", details: "Custom GI / aluminium ducting fabrication & installation as per site layout." },
      ]
    },
    {
      icon: <Building2 className="h-10 w-10" />,
      title: "Interior Design & HVAC Layout",
      description: "Complete interior designing services with integrated HVAC layout planning for optimal comfort and aesthetics.",
      image: "/images/services/interior-design.jpg",
      features: ["Interior Design", "HVAC Planning", "Layout Design"],
      consultation: true,
      booking: [
        { name: "Interior | Full Home Turnkey Interior", rate: 0, quote: true, unit: "project", details: "Residential turnkey interior. Approx Rs.1,100 - Rs.2,150 / sq.ft (premium finishes higher)." },
        { name: "Interior | Office / Commercial Interior", rate: 0, quote: true, unit: "project", details: "Approx Rs.900 - Rs.2,500 / sq.ft based on brand image & tech integration." },
        { name: "Interior | Modular Kitchen", rate: 0, quote: true, unit: "project", details: "Approx Rs.1.5 Lakh - Rs.6 Lakh+ based on layout, cabinets & countertop." },
        { name: "Interior | False Ceiling Design", rate: 0, quote: true, unit: "project", details: "Approx Rs.70 - Rs.250 / sq.ft (Gypsum/POP, with cove & lighting)." },
        { name: "HVAC Layout | HVAC System Design", rate: 0, quote: true, unit: "project", details: "Ducting, vent placement & equipment sizing. Approx 1.5% - 4% of HVAC install cost." },
        { name: "HVAC Layout | Thermal Comfort & Heat-Load Planning", rate: 0, quote: true, unit: "project", details: "Zone-wise heat-load analysis to size the right AC type (Split/Cassette/Ducted/VRF)." },
        { name: "HVAC Layout | Ducting & Ventilation Layout", rate: 0, quote: true, unit: "project", details: "Duct routing, grille & exhaust planning for seamless false-ceiling integration." },
        { name: "HVAC Layout | Central AC Design & Installation", rate: 0, quote: true, unit: "project", details: "Approx Rs.40,000 - Rs.1,50,000+ based on tonnage & ducting complexity." },
        { name: "Package | Comfort & Style Turnkey", rate: 0, quote: true, unit: "project", details: "Interior design + integrated HVAC planning & installation. Approx Rs.1,500 - Rs.2,800 / sq.ft." },
        { name: "Package | Smart Home Climate", rate: 0, quote: true, unit: "project", details: "Zoned control, app-based thermostats & air-quality sensors. Quoted on system complexity." },
        { name: "Package | Renovation & Retrofit", rate: 0, quote: true, unit: "project", details: "Interior refresh + HVAC upgrade with minimal disruption. Quoted after site assessment." },
        { name: "Package | Space Planning & 3D Visualization", rate: 0, quote: true, unit: "project", details: "Layout planning with 3D renders before execution. Approx Rs.15,000 - Rs.50,000+." },
      ]
    },
    {
      icon: <Fan className="h-10 w-10" />,
      title: "AHU (Air Handling Unit) Sale & Service",
      description: "Complete Air Handling Unit solutions including sales, installation, service and maintenance for all types of AHU systems.",
      image: "/images/services/ahu-unit.jpeg",
      features: ["VRF AHU", "Ductable AHU", "Mini AHU"]
    },
    {
      icon: <Cog className="h-10 w-10" />,
      title: "Compressor (New & Refurbished)",
      description: "High-quality new and refurbished compressors for all types of air conditioning systems with warranty and installation support.",
      image: "/images/services/compressor.jpeg",
      features: ["VRF", "Ductable", "Cassette", "Split", "Inverter", "Fix Speed"]
    },
    {
      icon: <Thermometer className="h-10 w-10" />,
      title: "Industrial Cooler",
      description: "Heavy-duty industrial cooling solutions for factories, warehouses, and large commercial spaces with various CFM options.",
      image: "/images/services/industrial-cooler.jpeg",
      features: ["High CFM", "Mid CFM", "3 Phase", "Single Phase"]
    }
  ];

  const filteredServices = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services
      .filter((s) => {
        const cat = SERVICE_CATEGORY[s.title] || "Other";
        const matchesCat = activeCategory === "All" || cat === activeCategory;
        const matchesSearch =
          !q ||
          s.title.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.features.some((f) => f.toLowerCase().includes(q)) ||
          (s.booking || []).some((b) => b.name.toLowerCase().includes(q));
        return matchesCat && matchesSearch;
      })
      .sort((a, b) => (b.booking ? 1 : 0) - (a.booking ? 1 : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeCategory]);

  return (
    <section id="services" className="py-20 bg-gray-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <Badge className="bg-amber-100 text-amber-700 mb-4">Our Services</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="services-title">
            Comprehensive HVAC Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From installation to maintenance, we provide end-to-end air conditioning services for all types of systems
          </p>
        </div>

        {/* Search + Category Filter */}
        <div className="mb-10 space-y-5">
          <div className="relative max-w-xl mx-auto">
            <Search className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services (e.g. VRF, gas leak, AMC, copper piping)..."
              className="pl-12 pr-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500 rounded-full shadow-sm"
              data-testid="services-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="services-search-clear"
                aria-label="clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-amber-500 text-black border-amber-500 shadow-md"
                    : "bg-white text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600"
                }`}
                data-testid={`services-category-${cat.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500" data-testid="services-result-count">
            Showing {filteredServices.length} of {services.length} services
          </p>
        </div>

        {/* Services Grid */}
        {filteredServices.length === 0 ? (
          <div className="text-center py-16" data-testid="services-no-results">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700 mb-1">No services found</p>
            <p className="text-gray-500 mb-4">Try a different keyword or category.</p>
            <button
              type="button"
              onClick={() => { setSearch(""); setActiveCategory("All"); }}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold px-5 py-2 rounded-full transition-colors"
              data-testid="services-reset-filters"
            >
              Reset filters
            </button>
          </div>
        ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices.map((service, index) => (
            <Card
              key={index}
              onClick={() => service.booking && setBookingService(service)}
              className={`group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 ${service.booking ? "cursor-pointer ring-2 ring-amber-400/70 hover:ring-amber-500" : ""}`}
              data-testid={`service-card-${index}`}
            >
              <div className="relative h-60 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover brightness-110 saturate-105 group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-amber-400">
                  {service.icon}
                </div>
                <div className="absolute top-3 right-3 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
                  <ShoppingCart className="h-3 w-3" /> Book Online
                </div>
              </div>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base font-semibold">{service.title}</CardTitle>
                <CardDescription className="text-gray-500 text-xs leading-relaxed line-clamp-2">{service.description}</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {service.features.map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium px-1.5 py-0">
                      {feature}
                    </Badge>
                  ))}
                </div>
                {service.booking && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setBookingService(service); }}
                    className="mb-2 inline-flex items-center gap-2 w-full justify-center bg-amber-500 hover:bg-amber-600 text-black font-semibold py-2 px-4 rounded-md transition-all"
                    data-testid={`service-book-${index}`}
                  >
                    <ShoppingCart className="h-5 w-5" /> {service.consultation ? "Book a Consultation" : "Book Now & See Rates"}
                  </button>
                )}
                <a 
                  href={getWhatsAppLink(`Hi! I'm interested in ${service.title} services. Please provide more details and a quote.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 w-full justify-center bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-md transition-all"
                  data-testid={`service-whatsapp-${index}`}
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Enquire on WhatsApp
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>

      <ServiceBookingDialog
        service={bookingService}
        open={!!bookingService}
        onOpenChange={(o) => { if (!o) setBookingService(null); }}
      />
    </section>
  );
};

export default ServicesSection;
